import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { generateId, formatBytes } from '@/lib/app-utils';
import { FileText, Trash2, Plus, Check, ArrowLeft, Calendar, User, Eye, Loader2, Filter, X, Upload } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useFileViewer } from '@/contexts/FileViewerContext';
import { cn } from '@/lib/utils';
import { lpoAPI } from '@/services/lpo';
import { projectsAPI } from '@/services/projects';


const ProjectLPOContractsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openViewer } = useFileViewer();
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form State
  const [docType, setDocType] = useState('Contract');
  const [description, setDescription] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]); // ✅ تغيير من file واحد لـ array
  const [isUploading, setIsUploading] = useState(false);
  const [loadingFileId, setLoadingFileId] = useState(null);

  // ✅ Filter State
  const [filterType, setFilterType] = useState('All');


  useEffect(() => {
    fetchData();
  }, [id]);


  const fetchData = async () => {
    try {
      setLoading(true);
      
      const projectRes = await projectsAPI.details(id);
      if (projectRes.success) {
        setProject({
          ...projectRes.data,
          projectName: projectRes.data.project_name || projectRes.data.projectName
        });
      }

      const docsRes = await lpoAPI.list(id);
      console.log('📋 LPO API Response:', docsRes);
      
      if (docsRes.success && docsRes.data) {
        const normalizedDocs = docsRes.data.map(doc => ({
          id: doc.id,
          type: doc.type || doc.status || 'Contract',
          description: doc.description || doc.vendor_name || '—',
          fileName: doc.file_name || doc.fileName || doc.document_path || 'file.pdf',
          fileSize: doc.file_size || doc.fileSize || 0,
          filePath: doc.file_path || doc.document_path,
          uploadedBy: doc.uploaded_by || doc.uploadedBy || doc.created_by || 'Unknown',
          uploaderId: doc.uploader_id || doc.uploaderId,
          uploadDate: doc.created_at || doc.upload_date || doc.uploadDate || new Date().toISOString()
        }));
        
        setDocuments(normalizedDocs);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error('Failed to load:', error);
      toast({ 
        title: "Error", 
        description: error.message || "Failed to load data", 
        variant: "destructive" 
      });
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };


  // ✅ دالة لاختيار ملفات متعددة
  const handleFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // التحقق من الحجم (250 ميجا لكل ملف)
    const maxSize = 250 * 1024 * 1024; // 250MB
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({ 
          title: "File Too Large", 
          description: `${file.name} exceeds 250MB limit`, 
          variant: "destructive" 
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setAttachedFiles(prev => [...prev, ...validFiles]);
      toast({ 
        title: "Files Attached", 
        description: `${validFiles.length} file(s) ready to upload` 
      });
    }
  };

  // ✅ حذف ملف معين من القائمة
  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };


  const handleSaveDocuments = async () => {
  if (attachedFiles.length === 0 || !description) {
    toast({ 
      title: "Validation Error", 
      description: "Please provide both a description and at least one file.", 
      variant: "destructive" 
    });
    return;
  }
  
  setIsUploading(true);
  let successCount = 0;
  
  try {
    // رفع كل ملف على حدة
    for (const file of attachedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('original_name', file.name);

        console.log(`📤 Uploading ${file.name}...`);

        const uploadResponse = await fetch('https://new.sbdemolition.online/api/lpo/upload-file.php', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: formData
        });

        const uploadResult = await uploadResponse.json();

        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'File upload failed');
        }

        console.log(`✅ File uploaded:`, uploadResult.data);

        // ✅ حفظ معلومات الملف - الوصف لوحده بدون اسم الملف
        const newDocData = {
          projectId: id,
          type: docType,
          description: description,  // ✅ الوصف لوحده بس
          file_path: uploadResult.data.filePath || uploadResult.data.file_path,
          file_name: file.name,      // ✅ اسم الملف منفصل
          file_size: file.size,
          uploadedBy: user?.name || 'Unknown',
          uploaderId: user?.id || null
        };

        console.log('📤 Sending to API:', newDocData);

        const response = await lpoAPI.create(newDocData);
        
        console.log('📥 API Response:', response);
        
        if (response.success) {
          successCount++;
          console.log(`✅ Document saved: ${file.name}`);
        } else {
          throw new Error(response.error || 'Failed to save document');
        }
      } catch (fileError) {
        console.error(`❌ Error uploading ${file.name}:`, fileError);
        toast({ 
          title: "Upload Failed", 
          description: `Failed to upload ${file.name}: ${fileError.message}`, 
          variant: "destructive" 
        });
      }
    }

    if (successCount > 0) {
      toast({ 
        title: "Success", 
        description: `${successCount} file(s) uploaded successfully`, 
        className: "bg-green-50 border-green-200 text-green-800" 
      });
      
      // Reset form
      setDocType('Contract');
      setDescription('');
      setAttachedFiles([]);
      
      fetchData();
    } else {
      toast({ 
        title: "Upload Failed", 
        description: "No files were uploaded successfully", 
        variant: "destructive" 
      });
    }
  } catch (err) {
    console.error('❌ Save error:', err);
    toast({ 
      title: "Error", 
      description: err.message || "Failed to save documents", 
      variant: "destructive" 
    });
  } finally {
    setIsUploading(false);
  }
};



  const handleDelete = async (doc) => {
    if (!window.confirm("Are you sure you want to delete this document? This action cannot be undone.")) {
      return;
    }
    
    try {
      const response = await lpoAPI.delete(doc.id);
      
      if (response.success) {
        toast({ title: "Document Deleted" });
        fetchData();
      } else {
        throw new Error(response.error || 'Failed to delete');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast({ 
        title: "Error", 
        description: error.message || "Failed to delete document", 
        variant: "destructive" 
      });
    }
  };


  const handleViewFile = async (doc) => {
    console.log('👁️ View File clicked:', doc);
    
    setLoadingFileId(doc.id);
    
    try {
      const filePath = doc.filePath || doc.file_path || doc.document_path;
      
      if (!filePath) {
        toast({ 
          title: "Error", 
          description: "File path not found", 
          variant: "destructive" 
        });
        setLoadingFileId(null);
        return;
      }

      const fileUrl = `https://new.sbdemolition.online/${filePath}`;
      console.log('📂 Opening file from server:', fileUrl);
      
      const fileName = doc.fileName || doc.file_name || doc.description || 'document.pdf';
      openViewer(fileUrl, fileName);
      
    } catch (error) {
      console.error('❌ View File Error:', error);
      toast({ 
        title: "Error", 
        description: `Failed to load file: ${error.message}`, 
        variant: "destructive" 
      });
    } finally {
      setLoadingFileId(null);
    }
  };


  const canDelete = (doc) => {
    if (!user) return false;
    return user.role === 'Admin' || doc.uploaderId === user.id || doc.uploadedBy === user.name;
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) return null;

  const filteredDocs = filterType === 'All' 
    ? documents 
    : documents.filter(doc => doc.type === filterType);

  const sortedDocs = filteredDocs.sort((a, b) => 
    new Date(b.uploadDate) - new Date(a.uploadDate)
  );

  const typeCounts = {
    All: documents.length,
    Contract: documents.filter(d => d.type === 'Contract').length,
    LPO: documents.filter(d => d.type === 'LPO').length,
    Report: documents.filter(d => d.type === 'Report').length,
    Quotation: documents.filter(d => d.type === 'Quotation').length,
    Invoice: documents.filter(d => d.type === 'Invoice').length,
    Other: documents.filter(d => d.type === 'Other').length,
  };


  return (
    <>
      <Helmet><title>LPO & Contracts - {project.projectName}</title></Helmet>
      <div className="space-y-8 animate-in fade-in duration-500">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b pb-6">
          <div>
            <h1 className="text-3xl font-bold text-navy-steal">LPO & Contracts</h1>
            <p className="text-stone-500 mt-1">
              Manage legal documents, LPOs, and contracts for{' '}
              <span className="font-semibold text-safety-orange">{project.projectName}</span>
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(`/projects/${id}`)} className="group">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" /> 
            Back to Project
          </Button>
        </div>


        {/* Upload Section */}
        <Card className="border-t-4 border-t-safety-orange shadow-md bg-white">
          <CardHeader className="pb-3 border-b border-stone-100">
            <CardTitle className="text-lg flex items-center gap-2 text-navy-steal">
              <div className="p-2 bg-orange-100 rounded-md">
                <Plus className="w-5 h-5 text-safety-orange" />
              </div>
              Upload New Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5 space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-stone-700">Document Type</label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger className="w-full bg-slate-50 border-stone-200">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="LPO">LPO</SelectItem>
                      <SelectItem value="Report">Report</SelectItem>
                      <SelectItem value="Quotation">Quotation</SelectItem>
                      <SelectItem value="Invoice">Invoice</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-stone-700">Description</label>
                  <Input 
                    placeholder="e.g. Signed Main Contract v1.0" 
                    value={description} 
                    onChange={e => setDescription(e.target.value)} 
                    className="bg-slate-50 border-stone-200"
                  />
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-stone-700">File Attachments (Multiple)</label>
                  
                  {/* ✅ زر رفع الملفات */}
                  <div className="relative">
                    <input
                      type="file"
                      multiple
                      accept="*/*"
                      onChange={handleFilesSelect}
                      className="hidden"
                      id="file-upload-input"
                    />
                    <label
                      htmlFor="file-upload-input"
                      className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <Upload className="w-8 h-8 text-stone-400 mb-2" />
                      <p className="text-sm text-stone-600 font-medium">Click to upload files</p>
                      <p className="text-xs text-stone-400 mt-1">All file types supported • Max 250MB per file</p>
                    </label>
                  </div>

                  {/* ✅ عرض الملفات المرفقة */}
                  {attachedFiles.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-stone-200 rounded-lg p-3 bg-white">
                      <p className="text-xs font-semibold text-stone-600 mb-2">
                        {attachedFiles.length} file(s) selected
                      </p>
                      {attachedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 p-2 rounded-lg">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="bg-white p-1.5 rounded border border-green-100">
                              <Check className="w-4 h-4 text-green-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-green-900 truncate">{file.name}</p>
                              <p className="text-xs text-green-700">{formatBytes(file.size)}</p>
                            </div>
                          </div>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-6 mt-2 border-t border-stone-100">
              <Button 
                onClick={handleSaveDocuments}
                className="bg-navy-steal hover:bg-charcoal min-w-[180px] text-white shadow-lg shadow-navy-steal/20 transition-all hover:scale-[1.02]"
                disabled={attachedFiles.length === 0 || !description || isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Save {attachedFiles.length > 0 ? `${attachedFiles.length} ` : ''}Document{attachedFiles.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>


        {/* Documents List */}
        <Card className="border-none shadow-md overflow-hidden">
          <CardHeader className="bg-slate-50 border-b border-stone-200">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <CardTitle className="text-xl text-navy-steal">Documents List</CardTitle>
              
              {/* Filter Section */}
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Filter className="w-4 h-4" />
                  <span className="font-medium">Filter:</span>
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[180px] bg-white border-stone-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Types ({typeCounts.All})</SelectItem>
                    <SelectItem value="Contract">Contract ({typeCounts.Contract})</SelectItem>
                    <SelectItem value="LPO">LPO ({typeCounts.LPO})</SelectItem>
                    <SelectItem value="Report">Report ({typeCounts.Report})</SelectItem>
                    <SelectItem value="Quotation">Quotation ({typeCounts.Quotation})</SelectItem>
                    <SelectItem value="Invoice">Invoice ({typeCounts.Invoice})</SelectItem>
                    <SelectItem value="Other">Other ({typeCounts.Other})</SelectItem>
                  </SelectContent>
                </Select>
                {filterType !== 'All' && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setFilterType('All')}
                    className="text-stone-500 hover:text-stone-700"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-white">
                <TableRow className="hover:bg-transparent border-b-2 border-stone-100">
                  <TableHead className="w-[150px] font-bold text-navy-steal uppercase text-xs tracking-wider">Type</TableHead>
                  <TableHead className="min-w-[200px] font-bold text-navy-steal uppercase text-xs tracking-wider">Description</TableHead>
                  <TableHead className="w-[250px] font-bold text-navy-steal uppercase text-xs tracking-wider">File Name</TableHead>
                  <TableHead className="w-[150px] font-bold text-navy-steal uppercase text-xs tracking-wider">Uploaded By</TableHead>
                  <TableHead className="w-[150px] font-bold text-navy-steal uppercase text-xs tracking-wider">Date</TableHead>
                  <TableHead className="w-[100px] text-right font-bold text-navy-steal uppercase text-xs tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedDocs.length > 0 ? sortedDocs.map((doc, idx) => (
                  <TableRow key={doc.id || idx} className="group hover:bg-slate-50/80 transition-colors border-b border-stone-50">
                    <TableCell className="align-top py-4">
                      <Badge variant="secondary" className={cn(
                        "border-none font-medium",
                        doc.type === 'Contract' ? "bg-blue-100 text-blue-700" :
                        doc.type === 'LPO' ? "bg-purple-100 text-purple-700" :
                        doc.type === 'Report' ? "bg-orange-100 text-orange-700" :
                        doc.type === 'Quotation' ? "bg-green-100 text-green-700" :
                        doc.type === 'Invoice' ? "bg-pink-100 text-pink-700" :
                        "bg-stone-100 text-stone-700"
                      )}>
                        {doc.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top py-4">
                      <p className="font-medium text-stone-800">{doc.description}</p>
                    </TableCell>
                    <TableCell className="align-top py-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-white p-2 rounded border border-stone-200 shadow-sm mt-0.5">
                          <FileText className="w-4 h-4 text-safety-orange" />
                        </div>
                        <div>
                          <p 
                            className="text-sm font-medium text-navy-steal break-all line-clamp-2 cursor-pointer hover:text-safety-orange hover:underline" 
                            title={doc.fileName}
                            onClick={() => handleViewFile(doc)}
                          >
                            {doc.fileName}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-[11px] text-stone-400">
                              {typeof doc.fileSize === 'number' ? formatBytes(doc.fileSize) : doc.fileSize}
                            </p>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="align-top py-4">
                      <div className="flex items-center gap-2 text-stone-600">
                        <User className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-sm">{doc.uploadedBy || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top py-4">
                      <div className="flex items-center gap-2 text-stone-600">
                        <Calendar className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-sm">{new Date(doc.uploadDate).toLocaleDateString()}</span>
                      </div>
                      <span className="text-[10px] text-stone-400 ml-5.5 pl-0.5">
                        {new Date(doc.uploadDate).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </TableCell>
                    <TableCell className="align-top py-4 text-right">
                      <div className="flex justify-end gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" 
                          onClick={() => handleViewFile(doc)} 
                          title="View File" 
                          disabled={loadingFileId === doc.id}
                        >
                          {loadingFileId === doc.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                        {canDelete(doc) && (
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-8 w-8 text-stone-400 hover:text-red-600 hover:bg-red-50" 
                            onClick={() => handleDelete(doc)} 
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-[300px] text-center">
                      <div className="flex flex-col items-center justify-center h-full text-stone-400">
                        <div className="bg-stone-50 p-4 rounded-full mb-3">
                          <FileText className="w-8 h-8 opacity-50" />
                        </div>
                        <p className="text-lg font-medium text-stone-600">
                          {filterType === 'All' ? 'No documents uploaded yet' : `No ${filterType} documents found`}
                        </p>
                        <p className="text-sm">
                          {filterType === 'All' ? 'Upload documents to get started' : 'Try selecting a different filter'}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
};


export default ProjectLPOContractsPage;
